// ============================================================================
// PayrollCharts (F-659, GC-E) — tab « Graphiques » de la grille salariale CE
// ============================================================================
// Port FIDELE du PoC components/salary/payroll-charts.tsx (296 L @ SHA 7e40e4a).
// Vues visuelles de la masse salariale (cout employeur total). INTERACTIF : la
// legende fait office de selecteur d'equipes (clic = inclure/exclure) ; les % se
// recalculent sur la selection. Les services specialises ont un donut isole par
// employe. Client-safe : ne recoit que des donnees serialisees (RealPayrollRow
// agregees CLIENT via aggregateByTeam/buildServiceBreakdowns).
//
// TRANSPOSITION (§GC-E, Regle #28) : Card/CardHeader/Section/StatCard shadcn ->
// StatCard local + cartes GS_COMPONENTS.card + en-tetes de section ; `cn` ->
// jointure locale ; Intl fr-CA en dur (formatCurrency/Percent) -> helpers locale
// (format.ts, P-CE18) ; couleurs via tokens --chart-cat-* (DonutChart, deja E2) ;
// libelles FR -> i18n FR+EN. Regle #4 : zero emoji.

import { useMemo, useState } from 'react';
import { GS_COMPONENTS, EmptyState } from '@gs/ui';
import { ChartPie } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { chartCatColor, DonutChart } from './DonutChart';
import { StatCard, type StatVariance } from './StatCard';
import { formatCad, formatSignedPct, formatPctPlain } from './format';
import type { ServiceBreakdown, TeamPayroll } from '../../lib/salary/payroll-by-team';
import type { BudgetRevenueCheck } from '../../lib/salary/payroll-targets';

/** Carte « plan vs budget » (M-682) — masse prevue vs revenu budgete + cible. */
export type BudgetCheckWithRevenue = BudgetRevenueCheck & { revenueBudgetYear: number };

export interface PayrollChartsProps {
  teams: TeamPayroll[];
  services: ServiceBreakdown[];
  year: number;
  /** Masse annuelle prevue (toutes equipes, boni exclu) vs revenu BUDGETE +
   *  cible admin (SERVEUR) ; null si aucun budget saisi. La carte ne suit PAS la
   *  selection de legende (cible d'entreprise). */
  budgetCheck: BudgetCheckWithRevenue | null;
}

export function PayrollCharts({ teams, services, year, budgetCheck }: PayrollChartsProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // Part d'un montant dans un total, format entier (« 42 % ») — « — » si vide.
  const pct = (part: number, total: number): string =>
    total <= 0 ? '—' : `${Math.round((part / total) * 100)} %`;

  // Couleur stable par equipe (index dans la liste triee), independante de la
  // selection courante pour que la legende et le donut restent coherents.
  const colorByTeam = useMemo(() => {
    const m = new Map<string, string>();
    teams.forEach((tm, i) => m.set(tm.teamName, chartCatColor(i)));
    return m;
  }, [teams]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(teams.map((tm) => tm.teamName)));

  const toggle = (name: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const selectedTeams = teams.filter((tm) => selected.has(tm.teamName));
  const totalCost = selectedTeams.reduce((s, tm) => s + tm.cost, 0);
  const totalBonusPotential = selectedTeams.reduce((s, tm) => s + tm.bonusPotential, 0);
  const totalHeadcount = selectedTeams.reduce((s, tm) => s + tm.headcount, 0);
  const avgCost = totalHeadcount > 0 ? totalCost / totalHeadcount : 0;
  const allSelected = selected.size === teams.length;

  const slices = selectedTeams.map((tm) => ({
    label: tm.teamName,
    value: tm.cost,
    color: colorByTeam.get(tm.teamName) ?? chartCatColor(0),
  }));

  if (teams.length === 0) {
    return (
      <EmptyState
        icon={<ChartPie size={48} aria-hidden="true" />}
        title={t('gest:grilleCe.graphiques.empty.title')}
        description={t('gest:grilleCe.graphiques.empty.description', { year })}
      />
    );
  }

  // Ecart vs cible de la carte budget : rouge si la masse pese plus que la cible.
  const budgetVariances: StatVariance[] | undefined =
    budgetCheck && budgetCheck.deltaPts !== null
      ? [
          {
            label: t('gest:grilleCe.graphiques.kpi.budget.varDelta'),
            value: formatSignedPct(budgetCheck.deltaPts, locale),
            tone: budgetCheck.deltaPts > 0 ? 'negative' : 'positive',
          },
          ...(budgetCheck.revenueShortfall > 0
            ? [
                {
                  label: t('gest:grilleCe.graphiques.kpi.budget.varShortfall'),
                  value: formatCad(budgetCheck.revenueShortfall, locale),
                  tone: 'negative' as const,
                },
                {
                  label: t('gest:grilleCe.graphiques.kpi.budget.varNeeded'),
                  value: formatCad(budgetCheck.revenueNeeded, locale),
                  tone: 'neutral' as const,
                },
              ]
            : []),
        ]
      : undefined;

  return (
    <div className="flex flex-col gap-8">
      {/* 5 KPI de la selection. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard
          accent
          label={t('gest:grilleCe.graphiques.kpi.mass.label')}
          value={formatCad(totalCost, locale)}
          hint={
            allSelected
              ? t('gest:grilleCe.graphiques.kpi.mass.hintAll')
              : t('gest:grilleCe.graphiques.kpi.mass.hintSome', {
                  selected: selectedTeams.length,
                  total: teams.length,
                })
          }
        />
        <StatCard
          label={t('gest:grilleCe.graphiques.kpi.bonus.label')}
          value={formatCad(totalBonusPotential, locale)}
          hint={t('gest:grilleCe.graphiques.kpi.bonus.hint')}
        />
        <StatCard
          label={t('gest:grilleCe.graphiques.kpi.headcount.label')}
          value={String(totalHeadcount)}
          hint={t('gest:grilleCe.graphiques.kpi.headcount.hint')}
        />
        <StatCard
          label={t('gest:grilleCe.graphiques.kpi.avg.label')}
          value={formatCad(avgCost, locale)}
          hint={t('gest:grilleCe.graphiques.kpi.avg.hint')}
        />
        <StatCard
          label={t('gest:grilleCe.graphiques.kpi.budget.label')}
          value={budgetCheck && budgetCheck.ratio !== null ? formatPctPlain(budgetCheck.ratio, locale) : '—'}
          tone={
            budgetCheck === null || budgetCheck.ratio === null
              ? 'neutral'
              : budgetCheck.ratio > budgetCheck.targetPct
                ? 'primary'
                : 'success'
          }
          hint={
            budgetCheck === null
              ? t('gest:grilleCe.graphiques.kpi.budget.hintNone', { year })
              : t('gest:grilleCe.graphiques.kpi.budget.hint', {
                  target: formatPctPlain(budgetCheck.targetPct, locale),
                  revenue: formatCad(budgetCheck.revenueBudgetYear, locale),
                })
          }
          variances={budgetVariances}
        />
      </div>

      {/* Repartition par equipe : donut + legende-selecteur. */}
      <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
              {t('gest:grilleCe.graphiques.byTeam.title')}
            </h2>
            <p className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:grilleCe.graphiques.byTeam.description')}
            </p>
          </div>
          {!allSelected ? (
            <button
              type="button"
              onClick={() => setSelected(new Set(teams.map((tm) => tm.teamName)))}
              className="touch-manipulation text-field font-medium text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              {t('gest:grilleCe.graphiques.byTeam.selectAll')}
            </button>
          ) : null}
        </div>

        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
          <div className="relative grid shrink-0 place-items-center">
            <DonutChart
              slices={slices}
              size={200}
              thickness={30}
              ariaLabel={t('gest:grilleCe.graphiques.byTeam.donutAria')}
            />
            <div className="pointer-events-none absolute flex flex-col items-center">
              <span className="text-head font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                {formatCad(totalCost, locale)}
              </span>
              <span className="text-meta text-slate-500 dark:text-slate-400">
                {t('gest:grilleCe.graphiques.byTeam.teamCount', { n: selectedTeams.length })}
              </span>
            </div>
          </div>

          <ul className="flex min-w-0 flex-1 flex-col gap-1">
            {teams.map((tm) => {
              const on = selected.has(tm.teamName);
              return (
                <li key={tm.teamName}>
                  <button
                    type="button"
                    onClick={() => toggle(tm.teamName)}
                    aria-pressed={on}
                    className={`flex w-full touch-manipulation items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-field transition-colors hover:bg-slate-100 dark:hover:bg-slate-800${
                      on ? '' : ' opacity-40'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className="size-3 shrink-0 rounded-full"
                      style={{ backgroundColor: colorByTeam.get(tm.teamName) }}
                    />
                    <span className={`min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200${on ? '' : ' line-through'}`}>
                      {tm.teamName}
                      {tm.isService ? (
                        <span className="ml-1.5 text-meta text-slate-500 dark:text-slate-400">
                          {t('gest:grilleCe.graphiques.byTeam.serviceTag')}
                        </span>
                      ) : null}
                    </span>
                    <span className="w-24 text-right font-medium tabular-nums text-slate-700 dark:text-slate-200">
                      {formatCad(tm.cost, locale)}
                    </span>
                    <span className="w-12 text-right text-meta tabular-nums text-slate-500 dark:text-slate-400">
                      {on ? pct(tm.cost, totalCost) : '—'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Services specialises : 1 donut par service, detail par employe. */}
      {services.length > 0 ? (
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
              {t('gest:grilleCe.graphiques.services.title')}
            </h2>
            <p className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:grilleCe.graphiques.services.description')}
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {services.map((svc) => {
              const empSlices = svc.employees.map((e, i) => ({
                label: e.name,
                value: e.cost,
                color: chartCatColor(i),
              }));
              return (
                <div key={svc.name} className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
                  <div className="flex flex-col gap-0.5">
                    <h3 className="text-body font-semibold text-slate-800 dark:text-slate-100">{svc.name}</h3>
                    <p className="text-meta text-slate-500 dark:text-slate-400">
                      {t('gest:grilleCe.graphiques.services.subtitle', {
                        total: formatCad(svc.total, locale),
                        n: svc.employees.length,
                      })}
                    </p>
                  </div>
                  <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-6">
                    <DonutChart
                      slices={empSlices}
                      size={150}
                      thickness={24}
                      ariaLabel={t('gest:grilleCe.graphiques.services.donutAria', { name: svc.name })}
                    />
                    <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
                      {svc.employees.map((e, i) => (
                        <li key={e.name} className="flex items-center gap-2 text-field">
                          <span
                            aria-hidden="true"
                            className="size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: chartCatColor(i) }}
                          />
                          <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{e.name}</span>
                          <span className="w-24 text-right font-medium tabular-nums text-slate-700 dark:text-slate-200">
                            {formatCad(e.cost, locale)}
                          </span>
                          <span className="w-12 text-right text-meta tabular-nums text-slate-500 dark:text-slate-400">
                            {pct(e.cost, svc.total)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
}
